import React, { useState, useEffect, useCallback } from "react";
import {
    Card, Table, Button, Modal, Form, Input, Upload, Tag,
    Popconfirm, message, Typography, Space, Empty, Tooltip
} from "antd";
import {
    PlusOutlined, DeleteOutlined, FileTextOutlined, FilePdfOutlined,
    FileExcelOutlined, UploadOutlined, RobotOutlined, ReloadOutlined, EyeOutlined
} from "@ant-design/icons";
import axiosInstance from "../../../configs";
import { getUserLevel, USER_LEVELS } from "../../../utils/rbac";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const FILE_ACCEPT = ".pdf,.xlsx,.xls,.docx,.doc,.txt,.md,.csv";

const sourceTypeConfig = {
    pdf: { color: "red", label: "PDF" },
    excel: { color: "green", label: "Excel" },
    word: { color: "blue", label: "Word" },
    text: { color: "purple", label: "Teks" },
    file: { color: "orange", label: "File" },
};

const AIKnowledge = () => {
    // ── Hooks MUST all be declared before any conditional return ──
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showText, setShowText] = useState(false);
    const [showFile, setShowFile] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [preview, setPreview] = useState(null);
    const [uploadFile, setUploadFile] = useState(null);
    const [textForm] = Form.useForm();
    const [fileForm] = Form.useForm();

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get("/ai-knowledge");
            setData(res.data.data || []);
        } catch { message.error("Gagal memuat data."); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ── Guard: only render content for SUPER_ADMIN ──
    const level = getUserLevel();
    if (level !== USER_LEVELS.SUPER_ADMIN) {
        return (
            <Card style={{ margin: 24 }}>
                <Empty description="Halaman ini hanya dapat diakses oleh Super Admin." />
            </Card>
        );
    }

    // ── Handlers ──
    const handleAddText = async (values) => {
        setSubmitting(true);
        try {
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            await axiosInstance.post("/ai-knowledge/text", { ...values, created_by: user.name || user.username });
            message.success("Knowledge teks berhasil disimpan.");
            textForm.resetFields();
            setShowText(false);
            fetchData();
        } catch (e) {
            message.error(e?.response?.data?.error || "Gagal menyimpan.");
        } finally { setSubmitting(false); }
    };

    const handleUploadFile = async () => {
        try {
            const values = await fileForm.validateFields();
            if (!uploadFile) { message.error("Pilih file terlebih dahulu."); return; }
            setSubmitting(true);
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            const fd = new FormData();
            fd.append("file", uploadFile);
            fd.append("title", values.title);
            fd.append("created_by", user.name || user.username || "");
            const res = await axiosInstance.post("/ai-knowledge/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            message.success(res.data.message || "File berhasil diupload.");
            fileForm.resetFields();
            setUploadFile(null);
            setShowFile(false);
            fetchData();
        } catch (e) {
            message.error(e?.response?.data?.error || "Gagal upload file.");
        } finally { setSubmitting(false); }
    };

    const handleDelete = async (id) => {
        try {
            await axiosInstance.delete(`/ai-knowledge/${id}`);
            message.success("Knowledge dihapus.");
            fetchData();
        } catch { message.error("Gagal menghapus."); }
    };

    const handlePreview = async (record) => {
        try {
            const res = await axiosInstance.get(`/ai-knowledge/${record.id}`);
            setPreview(res.data.data);
        } catch { message.error("Gagal memuat preview."); }
    };

    const columns = [
        { title: "No", width: 50, render: (_, __, i) => i + 1 },
        {
            title: "Judul", dataIndex: "title",
            render: (t, r) => (
                <Space>
                    {r.source_type === "pdf" && <FilePdfOutlined style={{ color: "#ff4d4f" }} />}
                    {r.source_type === "excel" && <FileExcelOutlined style={{ color: "#52c41a" }} />}
                    {r.source_type === "word" && <FileTextOutlined style={{ color: "#1890ff" }} />}
                    {(r.source_type === "text" || r.source_type === "file") && <FileTextOutlined style={{ color: "#722ed1" }} />}
                    <a onClick={() => handlePreview(r)}>{t}</a>
                </Space>
            )
        },
        {
            title: "Tipe", dataIndex: "source_type", width: 80,
            render: t => {
                const cfg = sourceTypeConfig[t] || { color: "default", label: (t || "?").toUpperCase() };
                return <Tag color={cfg.color}>{cfg.label}</Tag>;
            }
        },
        { title: "File", dataIndex: "file_name", width: 180, render: v => v || "-" },
        { title: "Oleh", dataIndex: "created_by", width: 130, render: v => v || "-" },
        {
            title: "Tanggal", dataIndex: "created_at", width: 120,
            render: v => v ? new Date(v).toLocaleDateString("id-ID") : "-"
        },
        {
            title: "Aksi", width: 90, align: "center",
            render: (_, r) => (
                <Space size={4}>
                    <Tooltip title="Preview">
                        <Button size="small" icon={<EyeOutlined />} onClick={() => handlePreview(r)} />
                    </Tooltip>
                    <Popconfirm title="Hapus knowledge ini?" onConfirm={() => handleDelete(r.id)} okText="Ya" cancelText="Batal">
                        <Tooltip title="Hapus">
                            <Button danger size="small" icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div style={{ padding: 24 }}>
            <Card
                title={
                    <Space>
                        <RobotOutlined style={{ fontSize: 20, color: "#722ed1" }} />
                        <Title level={4} style={{ margin: 0 }}>AI Knowledge Base</Title>
                    </Space>
                }
                extra={
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={fetchData}>Refresh</Button>
                        <Button icon={<PlusOutlined />} onClick={() => setShowText(true)}>+ Teks</Button>
                        <Button type="primary" icon={<UploadOutlined />} onClick={() => setShowFile(true)}>
                            + Upload File
                        </Button>
                    </Space>
                }
            >
                <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                    Tambahkan pengetahuan domain agar <b>Atlantik AI</b> lebih akurat.
                    Mendukung: <b>PDF, Excel (.xlsx), Word (.docx), TXT, CSV</b>.
                </Paragraph>
                <Table
                    rowKey="id"
                    dataSource={data}
                    columns={columns}
                    loading={loading}
                    size="small"
                    pagination={{ pageSize: 20 }}
                    locale={{ emptyText: <Empty description="Belum ada knowledge." /> }}
                />
            </Card>

            {/* Modal: Tambah Teks */}
            <Modal
                open={showText}
                title="Tambah Knowledge Teks"
                onCancel={() => { setShowText(false); textForm.resetFields(); }}
                onOk={() => textForm.submit()}
                okText="Simpan"
                confirmLoading={submitting}
                width={640}
            >
                <Form form={textForm} layout="vertical" onFinish={handleAddText}>
                    <Form.Item name="title" label="Judul" rules={[{ required: true }]}>
                        <Input placeholder="cth: Kebijakan kawasan berikat Atlantik" />
                    </Form.Item>
                    <Form.Item name="content" label="Isi Knowledge" rules={[{ required: true }]}>
                        <TextArea rows={10} placeholder="Tulis pengetahuan domain di sini..." showCount />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modal: Upload File */}
            <Modal
                open={showFile}
                title="Upload File Knowledge"
                onCancel={() => { setShowFile(false); fileForm.resetFields(); setUploadFile(null); }}
                onOk={handleUploadFile}
                okText="Upload & Simpan"
                confirmLoading={submitting}
                width={520}
            >
                <Form form={fileForm} layout="vertical">
                    <Form.Item name="title" label="Judul" rules={[{ required: true }]}>
                        <Input placeholder="cth: SOP Pembongkaran 2025" />
                    </Form.Item>
                    <Form.Item label="File (PDF / Excel / Word / TXT, max 20MB)">
                        <Upload
                            accept={FILE_ACCEPT}
                            beforeUpload={(file) => { setUploadFile(file); return false; }}
                            onRemove={() => setUploadFile(null)}
                            maxCount={1}
                            fileList={uploadFile ? [{ uid: "1", name: uploadFile.name, status: "done" }] : []}
                        >
                            <Button icon={<UploadOutlined />}>Pilih File</Button>
                        </Upload>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Format didukung: .pdf, .xlsx, .xls, .docx, .doc, .txt, .csv, .md
                        </Text>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modal: Preview */}
            <Modal
                open={!!preview}
                title={preview?.title}
                onCancel={() => setPreview(null)}
                footer={null}
                width={720}
            >
                {preview && (
                    <div style={{ maxHeight: 500, overflowY: "auto" }}>
                        <Space style={{ marginBottom: 8 }}>
                            <Tag color={sourceTypeConfig[preview.source_type]?.color || "default"}>
                                {sourceTypeConfig[preview.source_type]?.label || preview.source_type?.toUpperCase()}
                            </Tag>
                            {preview.file_name && <Text type="secondary">{preview.file_name}</Text>}
                            <Text type="secondary">· {new Date(preview.created_at).toLocaleString("id-ID")}</Text>
                        </Space>
                        <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, background: "#f5f5f5", padding: 12, borderRadius: 6 }}>
                            {preview.content}
                        </pre>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default AIKnowledge;
